// src/encounter/services/care-model-api.ts
// The ONE module that talks to the care-model contracts from the practitioner app. Wire shapes and
// the contract text live in `@/types/care-model-api`.
//
//   C5 · care plans   → Scrips.Patient, reached like every other `/api/Patients/...` call: the PM-API
//                       host (`env.apiBaseUrl`; dev-api ingress routes the `/api/Patients` prefix to
//                       the Patient service). Capital-P `Patients` on purpose — it is the ingress rule's
//                       spelling, and ASP.NET routing is case-insensitive either way.
//   C3 · advance      → Scrips.Workflows, its own host (`env.workflowsBaseUrl`, the Vite `/workflows`
//                       proxy locally), the same way the care-pathway feed reaches it.
//   C2 · enrol        → Scrips.Workflows, same host (PROD-3206, the physician assigns a care plan).
//   care-model list   → Scrips.Workflows' Elsa management API, same host: the read pm-react's
//                       `useCareModels` makes (`/v1/workflow-definitions` + one `/Latest` read each).
//
// Both go through the shared api-client, so bearer auth, refresh-on-401 and the org/practice/user
// headers are the app's single copy. Organisation scope is the server's job, from the token.
import { get, getWorkflows, postWorkflows } from '@/shared/api/api-client';
import { latestDefinitions, toCareModelOptions, type CareModelOption } from '../lib/care-model-options';
import type {
  AdvanceCareModelsRequest,
  AdvanceCareModelsResponse,
  CareModelEnrolmentResponse,
  CareModelEnrolmentWire,
  CarePlanWire,
  PatientEnrolmentWire,
  PatientEnrolmentsResponse,
  WorkflowDefinitionPageWire,
  WorkflowDefinitionWire,
} from '@/types/care-model-api';

export async function fetchCarePlans(patientId: string, signal?: AbortSignal): Promise<CarePlanWire[]> {
  const res = await get<unknown>(`/api/Patients/${encodeURIComponent(patientId)}/care-plans`, { signal });
  // The contract is a bare array. Anything else is a contract break, and it must surface as a failed
  // read — never as "this patient has no care plan", which is a clinical statement.
  if (!Array.isArray(res)) throw new Error('care-plans: response is not the C5 array');
  return res as CarePlanWire[];
}

export function advanceCareModels(body: AdvanceCareModelsRequest): Promise<AdvanceCareModelsResponse> {
  return postWorkflows<AdvanceCareModelsResponse>('/workflows/care-models/enrolments/advance', body);
}

/** The server's page cap for C4 (ListCareModelEnrolmentsQuery.MaxPageSize). */
export const ENROLMENTS_PAGE_SIZE = 200;

/** A ceiling on pages read, so a server that never ends its list cannot loop the reader. */
export const ENROLMENTS_MAX_PAGES = 25;

/**
 * C4 · ALL of the patient's ACTIVE enrolments, for each plan's current step, read page by page. The
 * list must be COMPLETE: the step join shows a step only when a model has exactly one active
 * enrolment, so a missed page could make an ambiguous join look unique.
 *   · With a `total` (on any page; it holds for the pages after it): read until it is reached; a list
 *     that ends before it is a failed read.
 *   · Without one: read until a page shorter than the page size.
 * Anything but the `{ data: [...] }` envelope, a short list, or too many pages throws; the readout
 * then shows no step, never a wrong one.
 */
export async function fetchPatientEnrolments(patientId: string, signal?: AbortSignal): Promise<PatientEnrolmentWire[]> {
  const all: PatientEnrolmentWire[] = [];
  // A total declared on any page binds the rest of the read: a later page that omits it does not undo it.
  let declared: number | null = null;
  for (let page = 1; page <= ENROLMENTS_MAX_PAGES; page++) {
    const res = await getWorkflows<PatientEnrolmentsResponse>(
      `/workflows/care-models/enrolments?patientId=${encodeURIComponent(patientId)}&page=${page}&pageSize=${ENROLMENTS_PAGE_SIZE}`,
      { signal },
    );
    if (!res || !Array.isArray(res.data)) throw new Error('enrolments: response is not the C4 envelope');
    all.push(...res.data);
    if (typeof res.total === 'number' && Number.isFinite(res.total) && res.total >= 0) declared = res.total;
    if (declared !== null) {
      if (all.length >= declared) return all;
      if (res.data.length === 0) throw new Error('enrolments: the list ended before its declared total');
    } else if (res.data.length < ENROLMENTS_PAGE_SIZE) {
      return all;
    }
  }
  throw new Error('enrolments: more pages than the reader accepts');
}

/** How many single-definition reads `fetchCareModels` runs at once (pm-react's TENANT_READ_CONCURRENCY). */
export const DEFINITION_READ_CONCURRENCY = 5;

/** The care-model list read: the published version of each definition (see `fetchCareModels`). */
export const CARE_MODEL_LIST_PATH = '/v1/workflow-definitions?version=Published';

/**
 * The care models this organisation can assign: published, scoped to an organisation, automations out.
 * The list carries no tenant, tag or variables, so each published model is read once more (`/Published`).
 * PUBLISHED versions only, from the list read on (PROD-3104, 4 Nov): Elsa's list answers the LATEST version of each model by
 * default, so a newer DRAFT (a v3 being edited) hid a model whose v2 is published and is what an enrolment runs
 * (EnrolPatientInCareModel loads VersionOptions.Published). So the list asks for `version=Published`, keeps only published rows
 * before taking one per model, and reads each model's detail from its published version, not the draft's.
 * A failed single read leaves that model out (an unknown tenant is never offered); when EVERY read fails the
 * engine is not answering, and that throws, so the dialog says the list could not load instead of "none".
 */
export async function fetchCareModels(signal?: AbortSignal): Promise<CareModelOption[]> {
  const page = await getWorkflows<WorkflowDefinitionPageWire>(CARE_MODEL_LIST_PATH, { signal });
  if (!page || !Array.isArray(page.items)) throw new Error('workflow-definitions: response is not the { items } page');
  const rows = latestDefinitions(page.items.filter((r) => r.isPublished));
  const details = new Map<string, WorkflowDefinitionWire | null>();
  let failures = 0;
  let lastError: unknown = null;
  let next = 0;
  const worker = async () => {
    while (next < rows.length) {
      const id = rows[next++].definitionId as string;
      try {
        details.set(id, await getWorkflows<WorkflowDefinitionWire>(`/v1/workflow-definitions/${encodeURIComponent(id)}/Published`, { signal }));
      } catch (error) {
        if (signal?.aborted) throw error;
        failures++;
        lastError = error;
        details.set(id, null);
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(DEFINITION_READ_CONCURRENCY, rows.length) }, worker));
  if (rows.length > 0 && failures === rows.length) throw lastError;
  return toCareModelOptions(rows, details);
}

/** The answer named no enrolment: the call may still have enrolled, so it is "not confirmed". */
export class EnrolmentUnconfirmedError extends Error {
  constructor() {
    super('enrolment: the answer named no enrolment');
    this.name = 'EnrolmentUnconfirmedError';
  }
}

/**
 * C2 · enrol the patient in one care model. The body is the patient id only: the organisation and the app
 * (`enrolledVia` = this app's token client_id) come from the token. The `{ data }` envelope is unwrapped.
 */
export async function enrolInCareModel(definitionId: string, patientId: string): Promise<CareModelEnrolmentWire> {
  const res = await postWorkflows<CareModelEnrolmentResponse | CareModelEnrolmentWire | null>(
    `/workflows/care-models/${encodeURIComponent(definitionId)}/enrolments`,
    { patientId },
  );
  const body = (res && typeof res === 'object' && 'data' in res ? res.data : res) as CareModelEnrolmentWire | null | undefined;
  if (!body || typeof body.enrolmentId !== 'string' || body.enrolmentId === '') throw new EnrolmentUnconfirmedError();
  return body;
}
